import { KeyboardEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '@tanstack/react-table';
import { FilterIcon } from 'assets/icons';
import syncIcon from 'assets/icons/sync.svg';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { UnixDatePicker } from 'modules/datepicker';
import { Input, Popover, RadioGroup } from 'modules/ui';

import { DispatchersStateFilter } from './DispatchersStateFilter';
import { LocationRadiusFilter } from './LocationRadiusFilter';
import { OfferStateFilter } from './OfferStateFilter';

import styles from './Filtering.module.scss';

type StringFilterValue = undefined | string;
type NumberRangeFilterValue = undefined | [StringFilterValue, StringFilterValue];

export enum FilteringType {
    Contains,
    NumberRange,
    DateRange,
    Select,
    DispatchersState,
    LocationRadius,
    OfferState,
}

export const Filtering = ({ column, inline = false }: { column: Column<any, unknown>; inline?: boolean }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const filter = column.columnDef.meta?.filter;

    if (!filter || !column.getCanFilter()) {
        return null;
    }

    const filterActive = !!column.getFilterValue();
    const content = getFilterContent(column, inline);
    const resetFilter = () => column.setFilterValue(null);

    if (inline) {
        return (
            <div className={styles['inline-filtering']}>
                <div className={styles['inline-filter-content']}>{content}</div>
                <button
                    className={styles['inline-reset-button']}
                    data-role="inline-filter-reset"
                    onClick={resetFilter}
                    type="button"
                >
                    <img alt="" aria-hidden="true" className={styles['inline-reset-icon']} src={syncIcon} />
                    {t('table.filtering.reset')}
                </button>
            </div>
        );
    }

    return (
        <Popover
            open={open}
            onOpenChange={(value) => setOpen(value)}
            Icon={<FilterIconWithActiveIndicator active={filterActive} />}
            label={column.columnDef.header as string}
            withHeader
        >
            <div
                className={styles.filtering}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => event.key === 'Enter' && setOpen(false)}
            >
                {content}
                <button className={styles['clear-button']} onClick={resetFilter}>
                    {t('table.filtering.clear')}
                </button>
            </div>
        </Popover>
    );
};

const FilterIconWithActiveIndicator = ({ active }: { active: boolean }) => {
    return (
        <div
            className={classNames(styles['icon-filter'], {
                [styles['icon-filter-active']]: active,
            })}
        >
            <FilterIcon height={16} width={16} />
        </div>
    );
};

const getFilterContent = (column: Column<any, unknown>, compact = false): ReactNode => {
    const filter = column.columnDef.meta?.filter;

    if (!filter) {
        return null;
    }

    switch (filter.type) {
        case FilteringType.Contains:
            return <ContainsFilter column={column} compact={compact} />;
        case FilteringType.NumberRange:
            return <NumberRangeFilter column={column} compact={compact} />;
        case FilteringType.Select:
            return <SelectFilter column={column} compact={compact} />;
        case FilteringType.DateRange:
            return <DateRangeFilter column={column} compact={compact} />;
        case FilteringType.DispatchersState:
            return <DispatchersStateFilter column={column} />;
        case FilteringType.LocationRadius:
            return <LocationRadiusFilter column={column} />;
        case FilteringType.OfferState:
            return <OfferStateFilter column={column} compact={compact} />;
        default:
            return null;
    }
};

const ContainsFilter = ({ column, compact = false }: { column: Column<any, unknown>; compact?: boolean }) => {
    const [curValue, setCurValue] = useState<string>(column.getFilterValue() as string);
    const filterValue = column.getFilterValue() as string | undefined;

    const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
        handleSearch(e.currentTarget.value);
        setCurValue(e.currentTarget.value);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearch = useCallback(
        debounce((value) => {
            column.setFilterValue(value);
        }, 300),
        [],
    );

    useEffect(() => {
        setCurValue(filterValue ?? '');
    }, [filterValue]);

    const { t } = useTranslation();
    return (
        <div className={classNames({ [styles.compactTextField]: compact })}>
            <Input
                className={styles.input}
                value={curValue}
                onChange={onSearchChange}
                placeholder={t('table.filtering.contains')}
            />
        </div>
    );
};

const NumberRangeFilter = ({ column, compact = false }: { column: Column<any, unknown>; compact?: boolean }) => {
    const { t } = useTranslation();
    const [min, max] = (column.getFilterValue() as NumberRangeFilterValue) || [];

    return (
        <div className={styles['number-range-filter']}>
            <div className={classNames({ [styles.compactField]: compact })}>
                <Input
                    className={styles.input}
                    placeholder={compact ? t('table.filtering.minShort') : t('table.filtering.min')}
                    value={min ?? ''}
                    onChange={(event) =>
                        column.setFilterValue((old: NumberRangeFilterValue) => [event.target.value, old?.[1]])
                    }
                />
            </div>
            <div className={classNames({ [styles.compactField]: compact })}>
                <Input
                    className={styles.input}
                    placeholder={compact ? t('table.filtering.maxShort') : t('table.filtering.max')}
                    value={max ?? ''}
                    onChange={(event) =>
                        column.setFilterValue((old: NumberRangeFilterValue) => [old?.[0], event.target.value])
                    }
                />
            </div>
        </div>
    );
};

const SelectFilter = ({ column, compact = false }: { column: Column<any, unknown>; compact?: boolean }) => {
    const value = (column.getFilterValue() as StringFilterValue) || '';
    const options = column.columnDef.meta?.filter?.options || [];
    return (
        <RadioGroup
            options={options}
            value={value}
            optionStyles={classNames({ [styles.compactOption]: compact })}
            onValueChange={(value) => column.setFilterValue(value)}
        />
    );
};

type DateRangeFilterValue = string[];
const DateRangeFilter = ({ column, compact = false }: { column: Column<any, unknown>; compact?: boolean }) => {
    const { t } = useTranslation();
    const [min, max] = (column.getFilterValue() as DateRangeFilterValue) || [];

    return (
        <div className={styles['number-range-filter']}>
            <div className={classNames({ [styles.compactDateField]: compact })}>
                <UnixDatePicker
                    value={min ?? ''}
                    onChange={(value) => {
                        column.setFilterValue((old: DateRangeFilterValue) => [value, old?.[1]]);
                    }}
                    maxValue={max}
                    placeholder={compact ? t('table.filtering.dateFromShort') : t('table.filtering.dateFrom')}
                    variant={compact ? 'table-inline' : 'default'}
                />
            </div>
            <div className={classNames({ [styles.compactDateField]: compact })}>
                <UnixDatePicker
                    value={max ?? ''}
                    onChange={(value) => {
                        column.setFilterValue((old: DateRangeFilterValue) => [old?.[0], value]);
                    }}
                    minValue={min}
                    placeholder={compact ? t('table.filtering.dateToShort') : t('table.filtering.dateTo')}
                    variant={compact ? 'table-inline' : 'default'}
                />
            </div>
        </div>
    );
};
